var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { AwesomeEvent } from '@philips-internal/oswald-ui-core';
import { ValidateEvent, ValidateEventListener } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { scopedWarningMessage } from '@philips-internal/oswald-ui-utils';
import BaseFieldAPI from './base-field-api.js';
class FieldAPI extends BaseFieldAPI {
    constructor() {
        super(...arguments);
        /** @event */
        this.onValidate = new ValidateEventListener();
    }
    /**
     * Returns if the text will generate ellipsis on overflow.
     */
    isEllipsis() {
        return Boolean(this.state.ellipsis);
    }
    /**
    * Sets if the text will generate ellipsis on overflow.
    *
    * @param ellipsis - If the text will generate ellipsis on overflow.
    */
    setEllipsis(ellipsis) {
        this.setState({ ellipsis });
    }
    /**
     * Gets the value of the Field.
     *
     * @returns the value of the Field.
     */
    getValue() {
        const data = this.getData();
        const entry = this.getEntry();
        return data[entry];
    }
    /**
     * Sets the value of the Field.
     *
     * @param value - The value for the Field.
     */
    setValue(value) {
        const { validatedOnChange, entry, data } = this.state;
        const oldValue = data[entry];
        data[entry] = value;
        this.setState({ data });
        if (validatedOnChange) {
            this.validate();
        }
        this.onChange.fire({ entry, oldValue, value });
    }
    /**
     * Checks if the Field was validated on change.
     *
     * @returns a boolean indicating if the Field was validated on change.
     */
    isValidatedOnChange() {
        return this.state.validatedOnChange;
    }
    /**
     * Sets if the Field was validated on change.
     *
     * @param validatedOnChange - If the Field was validated on change.
     */
    setValidatedOnChange(validatedOnChange) {
        this.setState({ validatedOnChange });
    }
    /**
     * Gets the array of validators configured to the Field.
     *
     * @returns the array of validators configured to the Field.
     */
    getValidators() {
        return this.state.validators;
    }
    /**
     * Sets the validators to be used by the Field.
     *
     * @param validators - The validators to be used by the Field.
     */
    setValidators(validators) {
        this.setState({ validators });
    }
    /**
     * Adds a new validator to the Field.
     *
     * @param validator - The new validator.
     */
    addValidator(validator) {
        const { validators } = this.state;
        this.setState({ validators: [...validators, validator] });
    }
    /**
     * Removes the specified validator from the Field.
     *
     * @param name - Name of the validator to be removed.
     */
    removeValidator(name) {
        const { validators = [] } = this.state;
        this.setState({ validators: validators.filter((validator) => validator.name !== name) });
    }
    /**
     * @deprecated - When trying to assert that a Field is valid, use the `@OnValidate` event instead.
     */
    isValid() {
        const { validate } = this.state.__validation;
        const [valid] = validate(this.getValue(), false);
        return valid;
    }
    /**
     * Runs the validations configured to the Field.
     *
     * @param showValidations - If the validations must be shown in the Field.
     *
     * @returns a boolean indicating if the Field is valid.
     */
    validate(showValidations = true) {
        const { entry, __validation } = this.state;
        const [valid, validations] = __validation.validate(this.getValue(), true, showValidations);
        const event = new ValidateEvent({ entry, valid, validations });
        this.onValidate.fire(event);
        return valid;
    }
    /**
     * Adds a new local validator to be used by the Field.
     *
     * @param name - The name of the local validator.
     * @param localValidator - The validator function.
     * @param options - The options for how this validator must behave.
     */
    addLocalValidator(name, localValidator, options) {
        const validator = {
            name,
            options,
            validate: localValidator,
        };
        const { localValidators } = this.state;
        if (name in localValidators) {
            scopedWarningMessage('field-api', `There is already a validator with the name ${name}, overwriting it now.`);
        }
        this.setState({ localValidators: { ...localValidators, [name]: validator } });
    }
    /**
     * Removes a local validator from the Field.
     *
     * @param name - Name of the validator to be removed.
     */
    removeLocalValidator(name) {
        const { localValidators } = this.state;
        const updatedLocalValidators = { ...localValidators };
        delete updatedLocalValidators[name];
        this.setState({ localValidators: updatedLocalValidators });
    }
    /**
     * Gets the helper text of the Field.
     *
     * @returns the helper text of the Field.
     */
    getHelperText() {
        return this.state.helperText;
    }
    /**
     * Sets the helper text of the Field.
     *
     * @param helperText - The helper text of the Field.
     */
    setHelperText(helperText) {
        this.setState({ helperText });
    }
    /**
     * Gets the information of the Field.
     *
     * @returns the information of the Field.
     */
    getInformation() {
        return this.state.information;
    }
    /**
     * Sets the information of the Field.
     *
     * @param information - The information of the Field.
     */
    setInformation(information) {
        this.setState({ information });
    }
    /**
     * Checks if the Field is visible.
     *
     * @returns a boolean indicating if the Field is visible.
     */
    getVisible() {
        return this.state.visible;
    }
    /**
     * Sets the visibility of the Field.
     *
     * @param visible - If the Field should be visible.
     */
    setVisible(visible) {
        this.setState({ visible });
    }
    /**
     * Checks if the Field label is being struckthrough on read only mode.
     *
     * @returns a boolean indicating if the Field label is being struckthrough on read only mode.
     */
    isStrikethroughLabel() {
        return Boolean(this.state.strikethroughLabel);
    }
    /**
     * Sets if the Field label should be struckthrough.
     *
     * @param strikethroughLabel - If the Field label should be struckthrough.
     */
    setStrikethroughLabel(strikethroughLabel) {
        this.setState({ strikethroughLabel });
    }
    /**
     * Checks if the Field value is being struckthrough.
     *
     * @returns a boolean indicating if the Field value is being struckthrough.
     */
    isStrikethroughValue() {
        return Boolean(this.state.strikethroughValue);
    }
    /**
     * Sets if the Field value should be struckthrough.
     *
     * @param strikethroughValue - If the Field value should be struckthrough.
     */
    setStrikethroughValue(strikethroughValue) {
        this.setState({ strikethroughValue });
    }
    /**
     * Gets the configured sensitiveness for the field.
     *
     * @returns the configured sensitiveness for the field.
     */
    getSensitiveData() {
        return this.state.sensitiveData;
    }
    /**
     * Sets if the data of the field should be hidden when in read only mode.
     *
     * @param sensitiveData - How sensitive the data is.
     */
    setSensitiveData(sensitiveData) {
        this.setState({ sensitiveData });
    }
    /**
     * Gets a map containing all children APIs.
     */
    getChildren() {
        const map = new Map();
        for (const api of Array.from(this.childrenAPIs.values())) {
            map.set(api.getAlias(), api);
        }
        return map;
    }
}
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], FieldAPI.prototype, "onValidate", void 0);
export default FieldAPI;
//# sourceMappingURL=field-api.js.map